import React, {useContext, useEffect, useMemo, useState} from 'react';

import {Button, Result, Steps} from 'antd';
import StepStructure from "../steps/structure/StepStructure";
import StepContent from "../steps/content/StepContent";
import StepPDF from "../steps/pdf/StepPDF";
import {GlobalSteps, mapStatusToProject, Status, StatusContext} from "../../services/types";
import { updateProject } from "../../services/api";
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const steps = [
    {
        title: 'Content Upload',
        content: <StepStructure/>,
    },
    {
        title: 'Content Comparison',
        content: <StepContent/>,
    },
    {
        title: 'PDF',
        content: <StepPDF/>,
    },
];

const stepIdToGlobalStep = (stepId: number): GlobalSteps | null => {
    switch (stepId) {
        case 1:
            return GlobalSteps.Load;
        case 2:
            return GlobalSteps.Comparison;
        case 3:
            return GlobalSteps.Generation;
        default:
            return null;
    }
};

const globalStepToStepId = (globalStep: GlobalSteps): number | null => {
    switch (globalStep) {
        case GlobalSteps.Load:
            return 1;
        case GlobalSteps.Comparison:
            return 2;
        case GlobalSteps.Generation:
            return 3;
        default:
            return null;
    }
};

const _booksSelectPage = () => {
    const statusContext = useContext(StatusContext);
    if (!statusContext) {
        throw new Error("missing context");
    }

    const { status, setStatus } = statusContext;
    const navigate = useNavigate();


    const next = () => {
        if (!status.projectID) {
            console.error("Project ID is missing");
            setStatus({
                ...status,
                globalStep: GlobalSteps.Fail,
                nextEnabled: false,
            });
            return;
        }

        updateProject(status.projectID, mapStatusToProject(status))
            .then(() => {
                setStatus({
                    ...status,
                    globalStep: status.globalStep + 1,
                    nextEnabled: false,
                });

                const nextStepId = globalStepToStepId((status.globalStep + 1) as GlobalSteps);
                if (nextStepId) {
                    navigate(`/steps/${nextStepId}`, { state: { wizardNav: true } });
                }
            })
            .catch(error => {
                console.error(error);
                setStatus({
                    ...status,
                    globalStep: GlobalSteps.Fail,
                    nextEnabled: false,
                })
            })
    };

    const contentStyle: React.CSSProperties = {
        lineHeight: '260px',
        textAlign: 'center',
        marginTop: 16,
    };

    const items = steps.map((item) => ({ key: item.title, title: item.title }));

    return (
        <>
            {
                status.globalStep === GlobalSteps.Fail ?
                    <Result
                        status="warning"
                        title="There are some problems with your book."
                    />: null
            }
            {
                status.globalStep < GlobalSteps.Fail ?
                    <>
                        <Steps current={status.globalStep} items={items} percent={status.percent} />
                        <div style={contentStyle}>{steps[status.globalStep].content}</div>
                        <div style={{ marginTop: 24 }}>
                            {status.globalStep === GlobalSteps.Load && (
                                <Button disabled={!status.nextEnabled} type="primary" onClick={() => next()}>
                                    Next
                                </Button>
                            )}
                        </div>
                    </>: null
            }
        </>
    );
  }


const StepsComponent: React.FC = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [status, setStatus] = useState<Status>({
        globalStep: GlobalSteps.Load,
        nextEnabled: false,
    });

    const requestedStepId = useMemo(() => {
        const raw = params.stepId;
        if (!raw) return null;
        const parsed = Number.parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : null;
    }, [params.stepId]);

    const isDirectBlocked = useMemo(() => {
        if (requestedStepId === null) return true;
        if (requestedStepId <= 1) return false;
        return (location.state as any)?.wizardNav !== true;
    }, [requestedStepId, location.state]);

    useEffect(() => {
        if (isDirectBlocked) return;
        if (requestedStepId === null) return;

        const mapped = stepIdToGlobalStep(requestedStepId);
        if (mapped === null) return;
        if (status.globalStep === GlobalSteps.Fail) return;

        if (status.globalStep !== mapped) {
            setStatus(prev => ({
                ...prev,
                globalStep: mapped,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedStepId, isDirectBlocked]);

    if (requestedStepId === null || stepIdToGlobalStep(requestedStepId) === null) {
        return (
            <Result
                status="error"
                title="Unknown step"
                subTitle="Use /steps/1, /steps/2 or /steps/3."
                extra={[
                    <Button key="to-first" type="primary" onClick={() => navigate('/steps/1', { replace: true })}>
                        Go to step 1
                    </Button>,
                ]}
            />
        );
    }

    if (isDirectBlocked) {
        return (
            <Result
                status="error"
                title="Step is unavailable"
                subTitle="For now, opening step 2/3 directly is not supported. Start from step 1."
                extra={[
                    <Button key="to-first" type="primary" onClick={() => navigate('/steps/1', { replace: true })}>
                        Go to step 1
                    </Button>,
                ]}
            />
        );
    }

    return (
        <StatusContext.Provider value={{ status, setStatus }}>
            <_booksSelectPage/>
        </StatusContext.Provider>
    )
}

export default StepsComponent;